/**
 * tw-deals-weeks.js
 * 12 Deals of Christmas 2026 — campaign weeks section JavaScript.
 *
 * Responsibilities:
 *  1. Re-compute deal states (expired / live / upcoming) client-side from
 *     Unix timestamps (store timezone via Liquid), correcting stale Liquid
 *     output from Shopify's full-page cache.
 *  2. Promote the single live week into the section header and featured panel.
 *  3. Drive an optional countdown timer to the next upcoming week's start.
 *  4. Handle grid tile clicks to open the relevant popup (live or expired).
 *  5. Popup open / close (Esc, backdrop click, focus management, focus trap).
 *  6. Code-deal "Reveal code" reveal + clipboard copy.
 *  7. Clean everything up on shopify:section:unload.
 */

(function () {
  'use strict';

  var STATE_LIVE     = 'live';
  var STATE_EXPIRED  = 'expired';
  var STATE_UPCOMING = 'upcoming';

  // ── Per-section initialiser ──────────────────────────────────────────────
  function initSection(root) {
    var sectionId      = root.getAttribute('data-tw-deals-section');
    var tiles          = Array.prototype.slice.call(root.querySelectorAll('[data-tw-deals-tile]'));
    var weekLabel      = root.querySelector('[data-tw-deals-week-label]');
    var countdownEl    = root.querySelector('[data-tw-deals-countdown]');
    var countdownTimer = countdownEl
      ? countdownEl.querySelector('[data-tw-deals-countdown-timer]')
      : null;
    var featurePanels  = Array.prototype.slice.call(root.querySelectorAll('[data-tw-deals-feature]'));
    var popupEls       = Array.prototype.slice.call(root.querySelectorAll('[data-tw-deals-popup]'));
    var revealBtns     = Array.prototype.slice.call(root.querySelectorAll('[data-tw-deals-reveal]'));
    var copyBtns       = Array.prototype.slice.call(root.querySelectorAll('[data-tw-deals-copy]'));

    var countdownHandle = null;
    var lastFocus       = null;
    var liveWeekNum     = null;
    var nextStartMs     = null;

    // ── Date helpers (Unix seconds from Liquid = store timezone) ─────────
    function parseStoreTimestamp(str) {
      if (!str || !String(str).trim()) return null;
      var sec = parseInt(String(str).trim(), 10);
      if (isNaN(sec)) return null;
      return sec * 1000;
    }

    function getTileStartMs(tile) {
      return parseStoreTimestamp(tile.getAttribute('data-tw-deals-start-ts') || '');
    }

    function getTileEndMs(tile) {
      return parseStoreTimestamp(tile.getAttribute('data-tw-deals-end-ts') || '');
    }

    // ── State computation ─────────────────────────────────────────────────
    function computeState(tile) {
      var override = tile.getAttribute('data-tw-deals-override') || 'auto';
      if (override !== 'auto') return override;

      var now      = Date.now();
      var startMs  = getTileStartMs(tile);
      var endMs    = getTileEndMs(tile);

      if (startMs === null || endMs === null) return STATE_UPCOMING;
      if (now >= startMs && now < endMs)      return STATE_LIVE;
      if (now >= endMs)                       return STATE_EXPIRED;
      return STATE_UPCOMING;
    }

    // ── Apply state to tile ───────────────────────────────────────────────
    function applyTileState(tile, state) {
      tile.classList.remove(
        'tw-deals-weeks__tile--live',
        'tw-deals-weeks__tile--expired',
        'tw-deals-weeks__tile--upcoming'
      );
      tile.classList.add('tw-deals-weeks__tile--' + state);

      var weekNum    = tile.getAttribute('data-tw-deals-tile');
      var actionEl   = tile.querySelector('.tw-deals-weeks__tile-action');
      var stateLabel = tile.querySelector('.tw-deals-weeks__tile-state-label');

      if (state === STATE_LIVE || state === STATE_EXPIRED) {
        tile.setAttribute('tabindex', '0');
        tile.removeAttribute('aria-hidden');
        tile.setAttribute(
          'aria-label',
          'Week ' + weekNum + ', ' + (state === STATE_LIVE ? 'Live' : 'Expired')
        );
      } else {
        tile.removeAttribute('tabindex');
        tile.setAttribute('aria-hidden', 'true');
      }

      if (actionEl) {
        if (state === STATE_EXPIRED)     { actionEl.textContent = 'Read More'; actionEl.style.display = ''; }
        else if (state === STATE_LIVE)   { actionEl.textContent = 'Unlock Now'; actionEl.style.display = ''; }
        else                             { actionEl.style.display = 'none'; }
      }

      if (stateLabel) {
        if (state === STATE_EXPIRED)     stateLabel.textContent = 'Expired';
        else if (state === STATE_LIVE)   stateLabel.textContent = 'Weekly offer';
        else                             stateLabel.textContent = 'Coming Soon';
      }
    }

    // ── Featured panels ───────────────────────────────────────────────────
    function showFeature(weekNum) {
      featurePanels.forEach(function (panel) {
        panel.hidden = String(panel.getAttribute('data-tw-deals-feature')) !== String(weekNum);
      });
    }

    function hideAllFeatures() {
      featurePanels.forEach(function (panel) { panel.hidden = true; });
    }

    // ── Header ────────────────────────────────────────────────────────────
    function updateHeader(wNum) {
      if (!weekLabel) return;
      weekLabel.textContent = wNum ? 'Week ' + wNum : '';
    }

    // ── Main state recompute ──────────────────────────────────────────────
    function computeAllStates() {
      liveWeekNum = null;
      nextStartMs = null;

      tiles.forEach(function (tile) {
        var state   = computeState(tile);
        var weekNum = parseInt(tile.getAttribute('data-tw-deals-tile'), 10);

        applyTileState(tile, state);

        if (state === STATE_LIVE && liveWeekNum === null) {
          liveWeekNum = weekNum;
        }

        if (state === STATE_UPCOMING) {
          var ms = getTileStartMs(tile);
          if (ms !== null && (nextStartMs === null || ms < nextStartMs)) {
            nextStartMs = ms;
          }
        }
      });

      updateHeader(liveWeekNum);

      if (liveWeekNum !== null) {
        showFeature(liveWeekNum);
      } else {
        hideAllFeatures();
      }

      updateCountdown();
    }

    // ── Countdown ─────────────────────────────────────────────────────────
    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function renderCountdown() {
      if (!countdownTimer || nextStartMs === null) return;
      var diff    = Math.max(0, nextStartMs - Date.now());
      var days    = Math.floor(diff / 86400000);
      var hours   = Math.floor((diff % 86400000) / 3600000);
      var minutes = Math.floor((diff % 3600000)  / 60000);
      var seconds = Math.floor((diff % 60000)    / 1000);
      countdownTimer.textContent =
        pad(days) + 'd  ' + pad(hours) + 'h  ' + pad(minutes) + 'm  ' + pad(seconds) + 's';
    }

    function updateCountdown() {
      if (!countdownEl) return;

      var usedLiquidFallback = false;

      // Fall back to the Liquid-emitted data attribute if JS found no upcoming start
      if (nextStartMs === null) {
        var attrTs = countdownEl.getAttribute('data-tw-deals-next-start-ts') || '';
        var fallbackMs = parseStoreTimestamp(attrTs);
        if (fallbackMs !== null) {
          nextStartMs = fallbackMs;
          usedLiquidFallback = true;
        }
      }

      if (nextStartMs === null) {
        countdownEl.hidden = true;
        if (countdownHandle) clearInterval(countdownHandle);
        countdownHandle = null;
        return;
      }

      // Stale cached fallback (or forced-upcoming + past start): refresh once, no interval loop
      if (Date.now() >= nextStartMs) {
        if (usedLiquidFallback) {
          countdownEl.removeAttribute('data-tw-deals-next-start-ts');
        }
        nextStartMs = null;
        countdownEl.hidden = true;
        if (countdownHandle) clearInterval(countdownHandle);
        countdownHandle = null;
        computeAllStates();
        return;
      }

      countdownEl.hidden = false;
      renderCountdown();

      if (countdownHandle) clearInterval(countdownHandle);
      countdownHandle = setInterval(function () {
        if (Date.now() >= nextStartMs) {
          clearInterval(countdownHandle);
          countdownHandle = null;
          computeAllStates(); // deal just went live — refresh
          return;
        }
        renderCountdown();
      }, 1000);
    }

    // ── Popup helpers ─────────────────────────────────────────────────────
    function getPopupEl(weekNum) {
      return root.querySelector(
        '[data-tw-deals-popup="' + weekNum + '"]'
      );
    }

    function syncPopupExpiredActions(popup, isExpired) {
      popup.querySelectorAll('[data-tw-deals-reveal]').forEach(function (btn) {
        btn.disabled = isExpired;
        if (isExpired) {
          btn.setAttribute('aria-disabled', 'true');
          btn.hidden = false;
        } else {
          btn.removeAttribute('aria-disabled');
        }
      });

      popup.querySelectorAll('.tw-deals-weeks__code-chip').forEach(function (chip) {
        if (isExpired) chip.hidden = true;
      });

      popup.querySelectorAll('[data-tw-deals-copy]').forEach(function (btn) {
        btn.disabled = isExpired;
      });

      popup.querySelectorAll('[data-tw-deals-popup-cta]').forEach(function (cta) {
        if (cta.tagName === 'BUTTON') {
          cta.disabled = isExpired;
        } else if (cta.tagName === 'A') {
          if (isExpired) {
            var href = cta.getAttribute('href');
            if (href && !cta.hasAttribute('data-tw-deals-stored-href')) {
              cta.setAttribute('data-tw-deals-stored-href', href);
            }
            cta.removeAttribute('href');
            cta.setAttribute('aria-disabled', 'true');
            cta.setAttribute('tabindex', '-1');
          } else {
            var storedHref = cta.getAttribute('data-tw-deals-stored-href');
            if (storedHref) cta.setAttribute('href', storedHref);
            cta.removeAttribute('aria-disabled');
            cta.removeAttribute('tabindex');
          }
        }
      });
    }

    function openPopup(weekNum, state) {
      var popup = getPopupEl(weekNum);
      if (!popup) return;

      var isExpired = state === STATE_EXPIRED;

      lastFocus = document.activeElement;
      popup.classList.toggle('is-expired', isExpired);
      syncPopupExpiredActions(popup, isExpired);
      popup.hidden = false;
      document.documentElement.classList.add('tw-deals-popup-open');

      var closeBtn = popup.querySelector('.tw-deals-popup__close');
      if (closeBtn) closeBtn.focus();

      popup.addEventListener('keydown', trapFocus);
    }

    function closePopup(popup) {
      if (!popup || popup.hidden) return;
      syncPopupExpiredActions(popup, false);
      popup.hidden = true;
      popup.classList.remove('is-expired');
      document.documentElement.classList.remove('tw-deals-popup-open');
      popup.removeEventListener('keydown', trapFocus);

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
    }

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      var focusable = Array.prototype.slice.call(
        e.currentTarget.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(function (el) {
        return el.offsetParent !== null && !el.closest('[hidden]');
      });
      if (!focusable.length) { e.preventDefault(); return; }
      var first = focusable[0];
      var last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    // ── Tile interaction ──────────────────────────────────────────────────
    function onTileClick(e) {
      var tile  = e.currentTarget;
      var state = tile.classList.contains('tw-deals-weeks__tile--live')
        ? STATE_LIVE
        : tile.classList.contains('tw-deals-weeks__tile--expired')
          ? STATE_EXPIRED
          : null;
      if (!state) return;
      var weekNum = parseInt(tile.getAttribute('data-tw-deals-tile'), 10);
      openPopup(weekNum, state);
    }

    function onTileKeydown(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onTileClick({ currentTarget: e.currentTarget });
      }
    }

    tiles.forEach(function (tile) {
      tile.addEventListener('click', onTileClick);
      tile.addEventListener('keydown', onTileKeydown);
    });

    // ── Popup close (Esc) ─────────────────────────────────────────────────
    function onDocKeydown(e) {
      if (e.key !== 'Escape') return;
      var open = root.querySelector('.tw-deals-popup:not([hidden])');
      if (open) closePopup(open);
    }

    document.addEventListener('keydown', onDocKeydown);

    // ── Popup close buttons & backdrop ────────────────────────────────────
    function onCloseClick(e) {
      var popup = e.currentTarget.closest('.tw-deals-popup');
      if (popup) closePopup(popup);
    }

    popupEls.forEach(function (popup) {
      var closers = Array.prototype.slice.call(
        popup.querySelectorAll('[data-tw-deals-popup-close]')
      );
      closers.forEach(function (btn) {
        btn.addEventListener('click', onCloseClick);
      });
    });

    // ── Code reveal ───────────────────────────────────────────────────────
    var revealHandlers = [];
    revealBtns.forEach(function (btn) {
      var handler = function () {
        if (btn.disabled) return;
        var expiredPopup = btn.closest('.tw-deals-popup.is-expired');
        if (expiredPopup) return;

        var revealKey = btn.getAttribute('data-tw-deals-reveal');
        var chipEl    = root.querySelector('#tw-deals-code-' + revealKey);
        if (!chipEl) return;
        btn.hidden    = true;
        chipEl.hidden = false;
        // Focus the code text for accessibility
        var codeText = chipEl.querySelector('.tw-deals-weeks__code-text');
        if (codeText) codeText.setAttribute('tabindex', '-1');
      };
      btn.addEventListener('click', handler);
      revealHandlers.push({ el: btn, fn: handler });
    });

    // ── Clipboard copy ────────────────────────────────────────────────────
    var copyHandlers = [];
    copyBtns.forEach(function (btn) {
      var handler = function () {
        if (btn.disabled) return;
        if (btn.closest('.tw-deals-popup.is-expired')) return;

        var code = btn.getAttribute('data-tw-deals-copy') || '';
        if (!code) return;

        function onSuccess() {
          var prevLabel = btn.getAttribute('aria-label');
          btn.setAttribute('aria-label', 'Copied!');
          setTimeout(function () {
            btn.setAttribute('aria-label', prevLabel || 'Copy discount code');
          }, 2000);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(onSuccess).catch(fallback);
        } else {
          fallback();
        }

        function fallback() {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          try { document.execCommand('copy'); onSuccess(); } catch (err) { /* silent */ }
          document.body.removeChild(ta);
        }
      };
      btn.addEventListener('click', handler);
      copyHandlers.push({ el: btn, fn: handler });
    });

    // ── Init ──────────────────────────────────────────────────────────────
    computeAllStates();

    // ── Cleanup ───────────────────────────────────────────────────────────
    function destroy() {
      if (countdownHandle) clearInterval(countdownHandle);
      document.removeEventListener('keydown', onDocKeydown);

      tiles.forEach(function (tile) {
        tile.removeEventListener('click', onTileClick);
        tile.removeEventListener('keydown', onTileKeydown);
      });

      popupEls.forEach(function (popup) {
        var closers = Array.prototype.slice.call(
          popup.querySelectorAll('[data-tw-deals-popup-close]')
        );
        closers.forEach(function (btn) {
          btn.removeEventListener('click', onCloseClick);
        });
        popup.removeEventListener('keydown', trapFocus);
        closePopup(popup); // close if open
      });

      revealHandlers.forEach(function (item) {
        item.el.removeEventListener('click', item.fn);
      });

      copyHandlers.forEach(function (item) {
        item.el.removeEventListener('click', item.fn);
      });

      document.documentElement.classList.remove('tw-deals-popup-open');
    }

    return { destroy: destroy };
  }

  // ── Bootstrap & section lifecycle ──────────────────────────────────────
  var instances = {};

  function mount(sectionEl) {
    var id = sectionEl.getAttribute('data-tw-deals-section');
    if (!id || instances[id]) return;
    instances[id] = initSection(sectionEl);
  }

  function unmount(sectionId) {
    if (instances[sectionId]) {
      instances[sectionId].destroy();
      delete instances[sectionId];
    }
  }

  function init() {
    var sections = Array.prototype.slice.call(
      document.querySelectorAll('[data-tw-deals-section]')
    );
    sections.forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-tw-deals-section]');
    if (el) mount(el);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-tw-deals-section]');
    if (el) unmount(el.getAttribute('data-tw-deals-section'));
  });

})();
